import { format, impliedRate, parse } from '@pf/money';
import { keyBetween, newUuidv7 } from '@pf/sync-rules';
import { useParams } from '@tanstack/react-router';
import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import f from '../app/Dialog.module.css';
import { Dialog, Field } from '../app/Dialog.tsx';
import { type Me, useMe, useTrip, useTripCards } from '../app/data.ts';
import { cx } from '../kit/cx.ts';
import {
  BudgetMeter,
  Button,
  DataTag,
  hazardEdge,
  SegmentedToggle,
  ShareBar,
  StatusTag,
} from '../kit/index.ts';
import t from '../kit/type.module.css';
import {
  addGoal,
  deleteExpense,
  deleteGoal,
  deleteSettlement,
  type ExpenseDraft,
  payInstalment,
  type SplitChoice,
  saveExpense,
  settle,
  snapshots,
} from '../model/budget.ts';
import {
  amountIn,
  balance,
  CATEGORIES,
  categoryName,
  type Entry,
  flag,
  goalProgress,
  myTotal,
  sharedTotal,
  status,
  type TripModel,
  today,
} from '../model/trip.ts';
import type { Edit } from '../sync.ts';
import s from './Budget.module.css';

const money = (minor: number | null, currency: string) =>
  minor === null ? 'Rate pending' : format(minor, currency);
const short = (minor: number, currency: string) => {
  const units = minor / 10 ** (currency === 'JPY' || currency === 'KRW' ? 0 : 2);
  return Math.abs(units) >= 10_000
    ? `${format(0, currency).replace(/[\d.,]+/, '')}${(units / 1000).toFixed(1)}k`
    : format(minor, currency);
};

/** W-02 budget: totals, expenses in three currencies, daily spend, settle-up, goals, dues, categories. */
export function Budget() {
  const { tripId } = useParams({ from: '/app/trips/$tripId/budget' });
  const { model, save, error } = useTrip(tripId);
  const me = useMe();
  const code = useTripCards().cards.find((c) => c.id === tripId)?.code;
  const [dest, setDest] = useState<string | null>(null);
  const [editing, setEditing] = useState<Entry | 'new' | null>(null);
  const [settling, setSettling] = useState(false);
  const [goal, setGoal] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (error)
    return (
      <p className={cx(s.pad, t.bodyS)} role="alert">
        {error.message}
      </p>
    );
  if (!model || !me.data) return <p className={cx(s.pad, t.bodyS)}>Loading…</p>;
  const people = me.data;
  const mine = people.user.homeCurrency;
  const theirs = people.partner?.homeCurrency ?? mine;
  const nameOf = (id: string) =>
    id === people.user.id ? 'Me' : id === people.partner?.id ? people.partner.displayName || 'Partner' : '—';
  const run = async (edits: Edit[]) => {
    const refused = await save(edits);
    setNotice(refused > 0 ? "Some changes weren't saved; the screen shows what the server has." : null);
  };

  const destination = model.destinations.find((d) => d.id === dest) ?? null;
  const entries = destination
    ? model.entries.filter(
        (e) =>
          e.dateLocal &&
          (destination.startDate ?? '') <= e.dateLocal &&
          e.dateLocal <= (destination.endDate ?? '9999'),
      )
    : model.entries;
  const shown = { ...model, entries };
  const shared = sharedTotal(shown, mine);
  const sharedTheirs = sharedTotal(shown, theirs);
  const local = destination?.localCurrency ?? model.destinations[0]?.localCurrency ?? null;
  const owed = balance(model, people.user.id, mine);
  const tripGoal = model.goals.find((g) => !g.ownerUserId && !g.categoryId && g.period === 'trip');
  const dateToday = today(model.days.find((d) => d.date === today(d.tz))?.tz ?? null);

  return (
    <div className={s.page}>
      <header className={s.top}>
        <div>
          <div className={cx(t.readoutS, s.muted)}>
            {code ?? model.title} · {flag(model.startDate)} ▶ {flag(model.endDate)}
          </div>
          <h1 className={cx(t.displayL, s.title)}>Budget</h1>
        </div>
        {model.destinations.length > 1 && (
          <fieldset className={s.filters} aria-label="Destination">
            {[{ id: null, name: 'All' }, ...model.destinations].map((d) => (
              <button
                key={d.id ?? 'all'}
                type="button"
                aria-pressed={dest === d.id}
                className={cx(s.chip, t.bodyS, dest === d.id && s.chipOn)}
                onClick={() => setDest(d.id)}
              >
                {d.name}
              </button>
            ))}
          </fieldset>
        )}
        <Button variant="ink" leader icon={Plus} onClick={() => setEditing('new')}>
          Log
        </Button>
      </header>
      {notice && (
        <p className={cx(s.notice, t.bodyS)} role="status">
          {notice}
        </p>
      )}

      <section className={s.kpis} aria-label="Totals">
        <Kpi
          label="Shared · both see"
          value={money(shared.sum, mine)}
          sub={[
            theirs !== mine ? money(sharedTheirs.sum, theirs) : null,
            shared.pending ? `${shared.pending} rate pending` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        <Kpi
          label="My total"
          value={money(myTotal(shown, people.user.id, mine), mine)}
          sub={theirs !== mine ? money(myTotal(shown, people.user.id, theirs), theirs) : ''}
        />
        <Kpi
          label="Trip budget · shared"
          value={
            tripGoal
              ? `${short(goalProgress(model, tripGoal, people.user.id, dateToday), tripGoal.currency)} / ${short(tripGoal.amountMinor, tripGoal.currency)}`
              : '—'
          }
          sub={tripGoal ? '' : 'No trip budget yet'}
          {...(tripGoal && tripGoal.amountMinor > 0
            ? { meter: goalProgress(model, tripGoal, people.user.id, dateToday) / tripGoal.amountMinor }
            : {})}
        />
        <div className={s.kpi}>
          {/* The balance as a data tag, as on the apps (W-02, S-08). */}
          <DataTag
            label={owed < 0 ? 'You owe' : owed > 0 ? 'Owed to you' : 'Settled'}
            {...(owed !== 0 ? { value: format(Math.abs(owed), mine) } : {})}
          />
          <div className={s.kpiRow}>
            <span className={cx(t.caption, s.muted)}>Shared expenses only</span>
            {owed !== 0 && people.partner && (
              <Button
                variant="primary"
                leader
                size="small"
                icon={ArrowRight}
                onClick={() => setSettling(true)}
              >
                Settle
              </Button>
            )}
          </div>
        </div>
      </section>

      <div className={s.grid}>
        <div className={s.main}>
          <Expenses
            model={shown}
            people={people}
            local={local}
            onOpen={setEditing}
            nameOf={nameOf}
            today={model.days.some((d) => d.date === dateToday) ? dateToday : null}
          />
          <DailySpend model={model} currency={mine} today={dateToday} />
          <Settlements
            model={model}
            nameOf={nameOf}
            mine={mine}
            theirs={theirs}
            onDelete={(id) => void run([deleteSettlement(tripId, id)])}
          />
        </div>
        <aside className={s.side}>
          <Panel
            title="My goals"
            action={
              <Button variant="ghost" size="small" icon={Plus} onClick={() => setGoal(true)}>
                Goal
              </Button>
            }
          >
            {model.goals.length === 0 && <p className={cx(t.bodyS, s.muted, s.padS)}>No goals yet.</p>}
            {model.goals.map((g) => {
              const spent = goalProgress(model, g, people.user.id, dateToday);
              return (
                <div key={g.id} className={cx(s.goal, spent > g.amountMinor && hazardEdge)}>
                  <div className={s.goalHead}>
                    <span className={t.titleM}>
                      {g.categoryId ? categoryName(model, g.categoryId) : 'All spending'} · {g.period}
                      {!g.ownerUserId && <span className={cx(t.caption, s.muted)}> · shared</span>}
                    </span>
                    <button
                      type="button"
                      className={s.icon}
                      aria-label="Delete goal"
                      onClick={() => void run([deleteGoal(tripId, g.id, g.ownerUserId)])}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className={cx(t.readoutS, spent > g.amountMinor && s.owe)}>
                    {format(spent, g.currency)} / {format(g.amountMinor, g.currency)}
                  </div>
                  <BudgetMeter fraction={g.amountMinor > 0 ? spent / g.amountMinor : 0} />
                </div>
              );
            })}
          </Panel>
          <Due
            model={model}
            mine={mine}
            onPay={(e, id, paid) => void run([payInstalment(tripId, id, e.ownerUserId, paid)])}
          />
          <ByCategory model={shown} currency={mine} />
        </aside>
      </div>

      {editing && (
        <ExpenseDialog
          model={model}
          people={people}
          entry={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={async (edits) => {
            await run(edits);
            setEditing(null);
          }}
        />
      )}
      {settling && people.partner && (
        <SettleDialog
          owed={owed}
          mine={mine}
          theirs={theirs}
          theirOwed={balance(model, people.partner.id, theirs)}
          onClose={() => setSettling(false)}
          onSave={async (amountMinor) => {
            const partner = people.partner?.id as string;
            const pays = owed < 0;
            await run(
              settle(tripId, {
                from: pays ? people.user.id : partner,
                to: pays ? partner : people.user.id,
                amountMinor,
                currency: mine,
                date: dateToday,
                otherCurrency: theirs,
                impliedRate: impliedRate(owed, mine, balance(model, partner, theirs), theirs),
              }),
            );
            setSettling(false);
          }}
        />
      )}
      {goal && (
        <GoalDialog
          currency={local ?? mine}
          me={people.user.id}
          categories={model.categories}
          onClose={() => setGoal(false)}
          onSave={async (g) => {
            await run([addGoal(tripId, g)]);
            setGoal(false);
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, sub, meter }: { label: string; value: string; sub: string; meter?: number }) {
  return (
    <div className={s.kpi}>
      <div className={cx(t.label, s.muted)}>{label}</div>
      <div className={t.readoutL}>{value}</div>
      {sub && <div className={cx(t.readoutS, s.muted)}>{sub}</div>}
      {meter !== undefined && <BudgetMeter fraction={meter} />}
    </div>
  );
}

function Panel({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className={s.panel}>
      <div className={s.panelHead}>
        <h2 className={t.label}>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Expenses({
  model,
  people,
  local,
  onOpen,
  nameOf,
  today: onTrip = null,
}: {
  model: TripModel;
  people: Me;
  local: string | null;
  onOpen: (e: Entry) => void;
  nameOf: (id: string) => string;
  /** On the trip, the list opens on today (W-02). */
  today?: string | null;
}) {
  const [day, setDay] = useState<string>(
    onTrip && model.entries.some((e) => e.dateLocal === onTrip) ? onTrip : 'all',
  );
  const mine = people.user.homeCurrency;
  const theirs = people.partner?.homeCurrency;
  const dates = [...new Set(model.entries.map((e) => e.dateLocal ?? '—'))].sort();
  const rows = day === 'all' ? model.entries : model.entries.filter((e) => (e.dateLocal ?? '—') === day);
  const dayNumber = (date: string) => model.days.find((d) => d.date === date)?.number;
  const stopOf = (itemId: string | null) => {
    for (const d of model.days) for (const p of d.items) if (p.item.id === itemId) return p.stopId;
    return null;
  };
  return (
    <section className={s.block}>
      <div className={s.panelHead}>
        <h2 className={t.label}>Expenses</h2>
        <label className={cx(t.caption, s.muted)}>
          Day{' '}
          <select value={day} onChange={(e) => setDay(e.target.value)} className={s.select}>
            <option value="all">All days ({model.entries.length})</option>
            {dates.map((d) => (
              <option key={d} value={d}>
                {d === '—'
                  ? 'No date'
                  : `${flag(d)}${dayNumber(d) ? ` · Day ${String(dayNumber(d)).padStart(2, '0')}` : ''}`}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead className={t.label}>
            <tr>
              <th>Date</th>
              <th>Item</th>
              <th>Category</th>
              <th>Paid by</th>
              <th>Split</th>
              <th>Status</th>
              <th className={s.num}>{local ?? 'Local'}</th>
              <th className={s.num}>{mine}</th>
              {theirs && theirs !== mine && <th className={s.num}>{theirs}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className={s.muted}>
                  Nothing logged.
                </td>
              </tr>
            )}
            {rows.map((e) => {
              const st = status(e);
              const stop = stopOf(e.itemId);
              const due = e.instalments.find((i) => !i.paidAt)?.dueDate ?? e.dueDate;
              return (
                <tr
                  key={e.id}
                  onClick={() => onOpen(e)}
                  tabIndex={0}
                  onKeyDown={(k) => k.key === 'Enter' && onOpen(e)}
                >
                  <td className={t.readoutS}>
                    {flag(e.dateLocal)} {e.timeLocal ?? ''}
                  </td>
                  <td className={t.bodyS}>
                    {e.description || 'Untitled'}{' '}
                    {stop && <span className={cx(t.readoutS, s.muted)}>{stop}</span>}
                    {e.ownerUserId && <DataTag label="Mine" />}
                  </td>
                  <td className={t.bodyS}>{categoryName(model, e.categoryId)}</td>
                  <td className={t.bodyS}>{e.payers.map((p) => nameOf(p.userId)).join(' + ') || '—'}</td>
                  <td className={t.readoutS}>{e.ownerUserId ? '—' : splitLabel(e)}</td>
                  <td>
                    <StatusTag
                      status={st === 'due' ? 'due' : st === 'planned' ? 'planned' : 'paid'}
                      date={due ? flag(due) : null}
                    />
                  </td>
                  <td className={cx(t.readoutS, s.num)}>{format(e.amountMinor, e.currency)}</td>
                  <td className={cx(t.readoutS, s.num)}>{money(amountIn(e, mine), mine)}</td>
                  {theirs && theirs !== mine && (
                    <td className={cx(t.readoutS, s.num)}>{money(amountIn(e, theirs), theirs)}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function splitLabel(e: Entry) {
  if (e.splits.length === 0) return '—';
  if (e.splits.every((x) => x.method === 'equal'))
    return e.splits.map(() => Math.round(100 / e.splits.length)).join('/');
  if (e.splits.every((x) => x.method === 'percent')) return e.splits.map((x) => x.value).join('/');
  return 'Exact';
}

function DailySpend({ model, currency, today: now }: { model: TripModel; currency: string; today: string }) {
  if (model.days.length === 0) return null;
  const totals = model.days.map((d) => ({
    date: d.date,
    sum: model.entries
      .filter((e) => !e.ownerUserId && e.dateLocal === d.date)
      .reduce((a, e) => a + (amountIn(e, currency) ?? 0), 0),
  }));
  const max = Math.max(1, ...totals.map((x) => x.sum));
  const past = totals.filter((x) => x.date <= now && x.sum > 0);
  const avg = past.length ? past.reduce((a, x) => a + x.sum, 0) / past.length : 0;
  return (
    <section className={s.block}>
      <div className={s.panelHead}>
        <h2 className={t.label}>Daily spend · shared · {currency}</h2>
        {avg > 0 && (
          <span className={cx(t.readoutS, s.muted)}>Avg {short(Math.round(avg), currency)} / day</span>
        )}
      </div>
      <div
        className={s.spark}
        role="img"
        aria-label={`Shared spend per day, highest ${format(max, currency)}`}
      >
        {totals.map((x) => (
          <i
            key={x.date}
            title={`${flag(x.date)} · ${format(x.sum, currency)}`}
            style={{ height: `${Math.max(2, (x.sum / max) * 100)}%` }}
            className={cx(x.date === now && s.todayBar, x.date > now && s.future)}
          />
        ))}
      </div>
      <div className={cx(s.sparkAxis, t.readoutS, s.muted)}>
        <span>{flag(totals[0]?.date ?? null)}</span>
        <span>{flag(totals.at(-1)?.date ?? null)}</span>
      </div>
    </section>
  );
}

function Settlements({
  model,
  nameOf,
  mine,
  theirs,
  onDelete,
}: {
  model: TripModel;
  nameOf: (id: string) => string;
  mine: string;
  theirs: string;
  onDelete: (id: string) => void;
}) {
  if (model.settlements.length === 0) return null;
  const sorted = [...model.settlements].sort((a, b) => (b.dateLocal ?? '').localeCompare(a.dateLocal ?? ''));
  return (
    <section className={s.block}>
      <div className={s.panelHead}>
        <h2 className={t.label}>Settle-up · history</h2>
        <span className={cx(t.caption, s.muted)}>Rate implied by balance</span>
      </div>
      <ul className={s.plain}>
        {sorted.map((x) => {
          const other = x.currency === mine ? theirs : mine;
          const rate = x.rates[other];
          return (
            <li key={x.id} className={s.settleRow}>
              <span className={t.readoutS}>{flag(x.dateLocal)}</span>
              <span className={t.bodyS}>
                {nameOf(x.fromUserId)} → {nameOf(x.toUserId)}
              </span>
              <span className={cx(t.readoutS, s.num)}>{format(x.amountMinor, x.currency)}</span>
              <span className={cx(t.readoutS, s.muted)}>{rate ? `@ ${Number(rate).toFixed(4)}` : ''}</span>
              <button
                type="button"
                className={s.icon}
                aria-label="Delete settle-up"
                onClick={() => onDelete(x.id)}
              >
                <Trash2 size={14} />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Due({
  model,
  mine,
  onPay,
}: {
  model: TripModel;
  mine: string;
  onPay: (e: Entry, instalmentId: string, paid: boolean) => void;
}) {
  const due = model.entries.filter((e) => status(e) === 'due');
  return (
    <Panel title="Due">
      {due.length === 0 && <p className={cx(t.bodyS, s.muted, s.padS)}>Nothing due.</p>}
      {due.map((e) => {
        const open = e.instalments.filter((i) => !i.paidAt);
        const next = open[0];
        const amount = next?.amountMinor ?? e.amountMinor;
        return (
          <div key={e.id} className={cx(s.goal, hazardEdge)}>
            <div className={t.titleM}>{e.description}</div>
            <div className={cx(t.caption, s.muted)}>
              {next ? `Instalment ${e.instalments.indexOf(next) + 1} of ${e.instalments.length}` : 'Balance'}{' '}
              · {flag(next?.dueDate ?? e.dueDate)}
            </div>
            <div className={s.goalHead}>
              <span className={t.readoutS}>
                {format(amount, e.currency)} · {money(amountIn(e, mine, amount), mine)}
              </span>
              {next && (
                <Button variant="ghost" size="small" onClick={() => onPay(e, next.id, true)}>
                  Mark paid
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </Panel>
  );
}

function ByCategory({ model, currency }: { model: TripModel; currency: string }) {
  const by = new Map<string, number>();
  for (const e of model.entries) {
    if (e.ownerUserId) continue;
    const key = e.categoryId ?? 'other';
    by.set(key, (by.get(key) ?? 0) + (amountIn(e, currency) ?? 0));
  }
  const rows = [...by].sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <Panel title="By category · shared">
      {rows.map(([k, v]) => (
        <div key={k} className={s.cat}>
          <span className={t.bodyS}>{categoryName(model, k)}</span>
          <ShareBar fraction={v / max} />
          <span className={cx(t.readoutS, s.catAmount)}>{format(v, currency)}</span>
        </div>
      ))}
    </Panel>
  );
}

// ── Dialogs ──────────────────────────────────────────────────────────────────────

function ExpenseDialog({
  model,
  people,
  entry,
  onClose,
  onSave,
}: {
  model: TripModel;
  people: Me;
  entry: Entry | null;
  onClose: () => void;
  onSave: (edits: Edit[]) => Promise<void>;
}) {
  const me = people.user.id;
  const partner = people.partner?.id ?? null;
  const day = model.days.find((d) => d.date === today(d.tz));
  const [description, setDescription] = useState(entry?.description ?? '');
  const [currency, setCurrency] = useState(
    entry?.currency ??
      day?.destination?.localCurrency ??
      model.destinations[0]?.localCurrency ??
      people.user.homeCurrency,
  );
  const [amount, setAmount] = useState(
    entry ? format(entry.amountMinor, entry.currency).replace(/^[^\d-]+/, '') : '',
  );
  const [category, setCategory] = useState(entry?.categoryId ?? 'food');
  const [naming, setNaming] = useState(false);
  const [categoryText, setCategoryText] = useState('');
  const [categoryMine, setCategoryMine] = useState(false);
  const [newCategory, setNewCategory] = useState<{ id: string; name: string; mine: boolean } | null>(null);
  const [date, setDate] = useState(entry?.dateLocal ?? day?.date ?? model.startDate ?? '');
  const [time, setTime] = useState(entry?.timeLocal ?? '');
  const [state, setState] = useState(entry ? status(entry) : 'paid');
  const [dueDate, setDueDate] = useState(entry?.dueDate ?? '');
  const [personal, setPersonal] = useState(entry ? entry.ownerUserId != null : false);
  const [payer, setPayer] = useState(
    entry?.payers.length === 1
      ? (entry.payers[0]?.userId ?? me)
      : entry && entry.payers.length > 1
        ? 'both'
        : me,
  );
  const initialSplit = (): string => {
    if (!entry || entry.splits.length === 0 || entry.splits.every((x) => x.method === 'equal'))
      return 'equal';
    if (entry.splits.length === 1) return entry.splits[0]?.userId === me ? 'me' : 'partner';
    return entry.splits.every((x) => x.method === 'percent') ? 'percent' : 'equal';
  };
  const [splitKind, setSplitKind] = useState(initialSplit);
  const [myPercent, setMyPercent] = useState(entry?.splits.find((x) => x.userId === me)?.value ?? '50');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const currencies = [
    ...new Set(
      [
        currency,
        ...model.destinations.map((d) => d.localCurrency).filter(Boolean),
        people.user.homeCurrency,
        people.partner?.homeCurrency,
      ].filter(Boolean) as string[],
    ),
  ];

  const submit = async () => {
    const minor = parse(amount, currency);
    if (minor === null || minor <= 0) return setProblem(`Enter an amount in ${currency}.`);
    if (!description.trim()) return setProblem('Say what it was for.');
    const both = partner ? [me, partner] : [me];
    let split: SplitChoice = { kind: 'equal', users: both };
    if (splitKind === 'me') split = { kind: 'exact', byUser: { [me]: minor } };
    if (splitKind === 'partner' && partner) split = { kind: 'exact', byUser: { [partner]: minor } };
    if (splitKind === 'percent' && partner) {
      const p = Number(myPercent);
      if (!(p >= 0 && p <= 100)) return setProblem('My share must be 0–100%.');
      split = { kind: 'percent', byUser: { [me]: String(p), [partner]: String(100 - p) } };
    }
    let payers: Record<string, number> = { [payer === 'both' ? me : payer]: minor };
    if (payer === 'both' && partner) {
      const half = Math.floor(minor / 2);
      payers = { [me]: minor - half, [partner]: half };
    }
    const destination = model.destinations.find(
      (d) => (d.startDate ?? '') <= date && date <= (d.endDate ?? '9999'),
    );
    const draft: ExpenseDraft = {
      description: description.trim(),
      amountMinor: minor,
      currency,
      categoryId: category,
      dateLocal: date || null,
      timeLocal: time || null,
      tz: model.days.find((d) => d.date === date)?.tz ?? destination?.tz ?? null,
      destinationId: destination?.id ?? null,
      itemId: entry?.itemId ?? null,
      status: state,
      dueDate: state === 'due' ? dueDate || null : null,
      personalFor: personal ? me : null,
      payers,
      split,
    };
    setBusy(true);
    const targets = [
      people.user.homeCurrency,
      people.partner?.homeCurrency,
      destination?.localCurrency,
    ].filter(Boolean) as string[];
    const rates = await snapshots(currency, targets, date || null);
    // A category made here is saved with the expense filed under it (FR-BUD-03).
    const categoryEdits: Edit[] =
      newCategory && category === newCategory.id
        ? [
            {
              table: 'category',
              id: newCategory.id,
              tripId: model.id,
              ownerUserId: newCategory.mine ? me : null,
              fields: {
                name: newCategory.name,
                icon: 'tag',
                sortKey: keyBetween(model.categories.at(-1)?.sortKey ?? null, null),
              },
            },
          ]
        : [];
    await onSave([...categoryEdits, ...saveExpense(model.id, draft, rates, entry ?? undefined)]);
    setBusy(false);
  };

  return (
    <Dialog title={entry ? 'Expense' : 'Log an expense'} onClose={onClose} onSubmit={() => void submit()}>
      <SegmentedToggle
        label="Whose expense"
        options={['Ours', 'Mine']}
        value={personal ? 1 : 0}
        onChange={(i) => setPersonal(i === 1)}
      />
      <div className={s.amountRow}>
        <select
          aria-label="Currency"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          className={cx(s.select, t.readoutM)}
        >
          {currencies.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <input
          aria-label="Amount"
          inputMode="decimal"
          autoFocus
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className={cx(s.amount, t.readoutXL)}
          placeholder="0"
        />
      </div>
      <Field label="For">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={f.input}
          placeholder="Okutan Kiyomizu"
        />
      </Field>
      <fieldset className={s.cats} aria-label="Category">
        {[
          ...Object.entries(CATEGORIES),
          ...model.categories.map((c) => [c.id, c.name] as [string, string]),
          ...(newCategory ? [[newCategory.id, newCategory.name] as [string, string]] : []),
        ].map(([k, v]) => (
          <button
            key={k}
            type="button"
            aria-pressed={category === k}
            className={cx(s.chip, t.label, category === k && s.chipOn)}
            onClick={() => setCategory(k)}
          >
            {v}
          </button>
        ))}
        {!naming && !newCategory && (
          <button type="button" className={cx(s.chip, t.label)} onClick={() => setNaming(true)}>
            + New
          </button>
        )}
      </fieldset>
      {/* FR-BUD-03: a category of the trip's own, saved with this expense. */}
      {naming && (
        <div className={f.pair}>
          <Field label="New category">
            <input
              value={categoryText}
              onChange={(e) => setCategoryText(e.target.value)}
              className={f.input}
              placeholder="Merch"
            />
          </Field>
          <label className={cx(t.bodyS, s.check)}>
            <input
              type="checkbox"
              checked={categoryMine}
              onChange={(e) => setCategoryMine(e.target.checked)}
            />{' '}
            Only for me
          </label>
          <Button
            variant="outline"
            size="small"
            leader={false}
            disabled={!categoryText.trim()}
            onClick={() => {
              const id = newUuidv7();
              setNewCategory({ id, name: categoryText.trim(), mine: categoryMine });
              setCategory(id);
              setNaming(false);
            }}
          >
            Add
          </Button>
        </div>
      )}
      <div className={f.pair}>
        <Field label="Date">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={f.input} />
        </Field>
        <Field label="Time">
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={f.input} />
        </Field>
      </div>
      <div className={f.pair}>
        <Field label="Status">
          <select value={state} onChange={(e) => setState(e.target.value)} className={f.input}>
            <option value="paid">Paid</option>
            <option value="due">Due</option>
            <option value="planned">Planned</option>
          </select>
        </Field>
        {state === 'due' && (
          <Field label="Due on">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={f.input}
            />
          </Field>
        )}
      </div>
      {!personal && people.partner && (
        <div className={f.pair}>
          <Field label="Paid by">
            <select value={payer} onChange={(e) => setPayer(e.target.value)} className={f.input}>
              <option value={me}>Me</option>
              <option value={people.partner.id}>{people.partner.displayName || 'Partner'}</option>
              <option value="both">Both, half each</option>
            </select>
          </Field>
          <Field label="Split">
            <select value={splitKind} onChange={(e) => setSplitKind(e.target.value)} className={f.input}>
              <option value="equal">50/50</option>
              <option value="percent">By percent</option>
              <option value="me">All mine</option>
              <option value="partner">All {people.partner.displayName || 'partner'}'s</option>
            </select>
          </Field>
          {splitKind === 'percent' && (
            <Field label="My %">
              <input
                inputMode="decimal"
                value={myPercent}
                onChange={(e) => setMyPercent(e.target.value)}
                className={f.input}
              />
            </Field>
          )}
        </div>
      )}
      {problem && (
        <p role="alert" className={cx(t.bodyS, s.owe)}>
          {problem}
        </p>
      )}
      <div className={f.actions}>
        {entry && (
          <Button
            variant="ghost"
            icon={Trash2}
            type="button"
            onClick={() => void onSave(deleteExpense(model.id, entry))}
          >
            Delete
          </Button>
        )}
        <span className={f.spacer} />
        <Button variant="ghost" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Dialog>
  );
}

function SettleDialog({
  owed,
  mine,
  theirs,
  theirOwed,
  onClose,
  onSave,
}: {
  owed: number;
  mine: string;
  theirs: string;
  theirOwed: number;
  onClose: () => void;
  onSave: (minor: number) => Promise<void>;
}) {
  const [amount, setAmount] = useState(format(Math.abs(owed), mine).replace(/^[^\d-]+/, ''));
  const [problem, setProblem] = useState<string | null>(null);
  return (
    <Dialog
      title="Settle up"
      onClose={onClose}
      onSubmit={() => {
        const minor = parse(amount, mine);
        if (minor === null || minor <= 0) return setProblem(`Enter an amount in ${mine}.`);
        void onSave(minor);
      }}
    >
      <p className={t.bodyS}>
        {owed < 0 ? 'You pay' : 'You receive'} {format(Math.abs(owed), mine)}
        {theirs !== mine ? ` (${format(Math.abs(theirOwed), theirs)} on their side)` : ''}. A full settle-up
        zeroes both views.
      </p>
      <Field label={`Amount · ${mine}`}>
        <input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className={cx(f.input, t.readoutM)}
        />
      </Field>
      {problem && (
        <p role="alert" className={cx(t.bodyS, s.owe)}>
          {problem}
        </p>
      )}
      <div className={f.actions}>
        <span className={f.spacer} />
        <Button variant="ghost" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          Record payment
        </Button>
      </div>
    </Dialog>
  );
}

function GoalDialog({
  currency: initial,
  me,
  categories,
  onClose,
  onSave,
}: {
  currency: string;
  me: string;
  /** The trip's own categories (FR-BUD-03). */
  categories: TripModel['categories'];
  onClose: () => void;
  onSave: (g: {
    owner: string | null;
    categoryId: string | null;
    amountMinor: number;
    currency: string;
    period: string;
  }) => Promise<void>;
}) {
  const [category, setCategory] = useState<string>('');
  const [period, setPeriod] = useState('day');
  const [currency, setCurrency] = useState(initial);
  const [amount, setAmount] = useState('');
  const [shared, setShared] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  return (
    <Dialog
      title="Add a goal"
      onClose={onClose}
      onSubmit={() => {
        const minor = parse(amount, currency);
        if (minor === null || minor <= 0) return setProblem(`Enter an amount in ${currency}.`);
        void onSave({
          owner: shared ? null : me,
          categoryId: category || null,
          amountMinor: minor,
          currency,
          period,
        });
      }}
    >
      <div className={f.pair}>
        <Field label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={f.input}>
            <option value="">All spending</option>
            {[
              ...Object.entries(CATEGORIES),
              ...categories.map((c) => [c.id, c.name] as [string, string]),
            ].map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Per">
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className={f.input}>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="trip">Trip</option>
          </select>
        </Field>
      </div>
      <div className={f.pair}>
        <Field label="Currency">
          <input
            value={currency}
            maxLength={3}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            className={f.input}
          />
        </Field>
        <Field label="Amount">
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={cx(f.input, t.readoutM)}
          />
        </Field>
      </div>
      <label className={cx(f.check, t.bodyS)}>
        <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> Shared budget
        (both see it)
      </label>
      {problem && (
        <p role="alert" className={cx(t.bodyS, s.owe)}>
          {problem}
        </p>
      )}
      <div className={f.actions}>
        <span className={f.spacer} />
        <Button variant="ghost" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          Add goal
        </Button>
      </div>
    </Dialog>
  );
}
